import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import WidgetContainer from "../../Components/WidgetContainer";
import ChartContainer from "../../Components/ChartContainer";
import { DownloadDropdown, InfoTooltip } from "../../Components";
import { capitalizeWords, currencyFormatter, numberFormatter } from "../../MMM/MMMUtils";
import { CartesianGrid, Line, LineChart as LC, Tooltip, YAxis, XAxis } from "recharts";
import * as R from "ramda";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE,
} from "../../Components/Charts/ChartConstants";
import { colorMap as colors } from "../../utils/colors";
import { useDynamicYAxisWidth } from "../../Components/Charts/ChartUtils";
import "./ModelInsights.scss";
import LineChart from "../../Components/Charts/LineChart";
import { useCompanyInfo } from "../../redux/company";
import {
  decayDataFormatter,
  OUTCOME_FORMATTER_TYPE,
  saturationDataFormatter,
} from "../DataFormatters";
import { downloadPNG, exportToExcel } from "../../utils/download-utils";
import { ResponseCurves } from "@blisspointmedia/bpm-types/dist/GeoMMM";

const CHART_CONTAINER_HEIGHT = 516;
const MEDIAN_DOT_SIZE = 7;
const GRAY_COLOR = "#94a0b8";
const INACTIVE_COLOR = "#d3d3d3"; //neutral200
// TODO: Fetch values Summary Metrics sheet
const LENGTH_OF_TIME_SERIES = {
  tinuititest: 104,
  olly: 103,
  dsw: 122,
  onceuponafarm: 106,
  milanlaser: 130,
};

interface ModelInsightsProps {
  outcomeVariable: "revenue" | "kpiVolume";
  responseCurvesRows: any[];
  adstockRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
  maxDimension: string;
}

export const ModelInsights: React.FC<ModelInsightsProps> = ({
  outcomeVariable,
  responseCurvesRows,
  adstockRows,
  channelBreakoutMap,
  maxDimension,
}) => {
  const { cid: company } = useCompanyInfo();
  const [formattedSatData, setFormattedSatData] = useState({} as any);
  const [formattedDecayData, setFormattedDecayData] = useState(
    {} as { newD: any[]; lines: any[]; groupedLines: any[] }
  );
  const [activeLine, setActiveLine] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);

  // Used to determine if a channel has multiple platforms for labeling charts
  const channelPlatformCounts = useMemo(() => {
    const counts: Record<string, Set<string>> = {};
    Object.values(channelBreakoutMap).forEach(({ channel, platform }) => {
      if (!counts[channel]) {
        counts[channel] = new Set();
      }
      counts[channel].add(platform);
    });
    return counts;
  }, [channelBreakoutMap]);

  const responseCurvesData: ResponseCurves[] = useMemo(() => {
    let responseCurves: ResponseCurves[] = [];
    if (!R.isEmpty(responseCurvesRows) && !R.isEmpty(R.keys(channelBreakoutMap))) {
      responseCurvesRows.forEach(row => {
        const {
          channel: breakout,
          spend,
          spend_multiplier: spendMultiplier,
          ci_hi: ciUpper,
          ci_lo: ciLower,
          mean: response,
        } = row;

        if (channelBreakoutMap[breakout.toLowerCase()]) {
          const { channel, platform, tactic, brand_nonbrand } = channelBreakoutMap[
            breakout.toLowerCase()
          ];

          responseCurves.push({
            name: breakout,
            channel: channel ?? null,
            platform: platform ?? null,
            tactic: tactic ?? null,
            brand_nonbrand: brand_nonbrand ?? null,
            spend: spend / LENGTH_OF_TIME_SERIES[company],
            spendMultiplier,
            ciLower: ciLower / LENGTH_OF_TIME_SERIES[company],
            ciUpper: ciUpper / LENGTH_OF_TIME_SERIES[company],
            response: response / LENGTH_OF_TIME_SERIES[company],
          });
        }
      });
    }
    return responseCurves;
  }, [responseCurvesRows, channelBreakoutMap, company]);

  const adstockData: any[] = useMemo(() => {
    let adstockData: any[] = [];
    if (!R.isEmpty(adstockRows) && !R.isEmpty(R.keys(channelBreakoutMap))) {
      adstockRows.forEach(row => {
        const {
          channel: breakout,
          time_units: period,
          distribution,
          ci_hi: ciHi,
          ci_lo: ciLo,
          mean: spendCarryover,
          is_int_time_unit: isIntTimeUnit,
        } = row;

        if (channelBreakoutMap[breakout.toLowerCase()]) {
          const { channel, platform, tactic, brand_nonbrand } = channelBreakoutMap[
            breakout.toLowerCase()
          ];
          if (distribution === "posterior" && isIntTimeUnit) {
            adstockData.push({
              name: breakout,
              channel: channel ?? null,
              platform: platform ?? null,
              tactic: tactic ?? null,
              brand_nonbrand: brand_nonbrand ?? null,
              spendCarryover,
              period,
              ciHi,
              ciLo,
            });
          }
        }
      });
    }
    return adstockData;
  }, [adstockRows, channelBreakoutMap]);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + 5,
    yAxisWidthModifier: x => x + 8,
  });

  const CustomizedDot = props => {
    const { cx, cy, stroke, payload, saturationData } = props;

    if (
      saturationData.meanValues[payload.name].dataPoint !== payload.spend ||
      saturationData.meanValues[payload.name].mean === null
    ) {
      return null;
    }

    return (
      <svg>
        <polygon
          points={`${cx},${cy - MEDIAN_DOT_SIZE} ${cx + MEDIAN_DOT_SIZE},${cy} ${cx},${
            cy + MEDIAN_DOT_SIZE
          } ${cx - MEDIAN_DOT_SIZE},${cy}`}
          fill={stroke}
        />
      </svg>
    );
  };

  useEffect(() => {
    if (responseCurvesData && !R.isEmpty(responseCurvesData)) {
      setFormattedSatData(saturationDataFormatter(responseCurvesData, channelPlatformCounts));
    }
  }, [responseCurvesData, channelPlatformCounts]);

  useEffect(() => {
    if (adstockData && !R.isEmpty(adstockData)) {
      const { newD, lines, groupedLines } = decayDataFormatter(
        adstockData,
        "spendCarryover",
        channelPlatformCounts
      );
      setFormattedDecayData({ newD, lines, groupedLines });
    }
  }, [adstockData, channelPlatformCounts]);

  const colorMap = useMemo(() => {
    if (!R.isEmpty(formattedSatData)) {
      return colors(formattedSatData.newD);
    }
  }, [formattedSatData]);

  const handleLegendClick = useCallback(
    (line, group?) => {
      if (activeLine === line.name) {
        setActiveLine(null);
        setActiveGroup(null);
      } else {
        setActiveLine(line.name);
        setActiveGroup(group.channel);
      }
    },
    [activeLine]
  );

  const handleLegendEnter = useCallback(
    line => {
      hoveredLegend === line.name ? setHoveredLegend(null) : setHoveredLegend(line.name);
    },
    [hoveredLegend]
  );

  const tooltip = (
    <Tooltip
      content={({ active, label, payload }) => {
        if (active && payload && payload.length) {
          let items = R.sortBy(
            (item: any) => -Math.abs(item.value),
            payload?.map(item => ({
              label: item.name as string,
              value: item.value as number,
              color: item.stroke,
            })) || []
          );

          return (
            <div className="satChartTooltip">
              <div className="itemList">
                {items.map((item, i) => {
                  const { label, value, color } = item;
                  if (!value && value !== 0) {
                    return <></>;
                  }
                  const cpa = payload[0].payload.spend / payload[0].payload.response;

                  return (
                    <div className="itemRow" key={i}>
                      <div className="itemWrapper dataLabel">
                        <div></div>
                        <div style={{ display: "flex", alignItems: "center", padding: "0 12px" }}>
                          <div className="line" style={{ backgroundColor: color }} />
                          <div className="name">{`${label}: `}</div>
                        </div>
                      </div>
                      <div className="itemWrapper">
                        <div className="satTooltipHeaderItem">Mean Spend:</div>
                        <div className="value">
                          {currencyFormatter.format(
                            formattedSatData.meanValues[payload[0].payload.name].mean,
                            0
                          )}
                        </div>
                      </div>
                      <div className="itemWrapper">
                        <div className="satTooltipHeaderItem">Spend:</div>
                        <div className="value">
                          {currencyFormatter.format(payload[0].payload.spend, 0)}
                        </div>
                      </div>
                      <div className="itemWrapper">
                        <div className="satTooltipHeaderItem">{`${
                          outcomeVariable === "revenue" ? "Revenue" : "KPI Volume"
                        }:`}</div>
                        <div className="value">
                          {OUTCOME_FORMATTER_TYPE[outcomeVariable].format(
                            payload[0].payload.response,
                            0
                          )}
                        </div>
                      </div>
                      {outcomeVariable === "revenue" && (
                        <div className="itemWrapper">
                          <div className="satTooltipHeaderItem">ROAS:</div>
                          <div className="value">
                            {(payload[0].payload.response / payload[0].payload.spend).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {outcomeVariable === "kpiVolume" && (
                        <div className="itemWrapper">
                          <div className="satTooltipHeaderItem">CPA:</div>
                          <div className="value">{`${
                            cpa === Infinity ? 0 : currencyFormatter.format(cpa)
                          }`}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        return null;
      }}
      isAnimationActive={false}
    />
  );

  const buildLegendByGroupedArea = useCallback(
    groupedAreas => {
      return groupedAreas.map((group, i) => {
        const channelArea = group.areas[0];
        const areaName = channelArea.name;
        const isActiveArea = activeLine === null ? true : activeLine === areaName;
        const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === areaName;
        const activeColor = isActiveArea ? colorMap[areaName] : INACTIVE_COLOR;
        const hoverColor = isHoveredLegend ? colorMap[areaName] : INACTIVE_COLOR;
        return (
          <div
            className="legendItem"
            key={`satDecayLegenditem${i}`}
            onClick={
              maxDimension === "Channel" ? () => handleLegendClick(channelArea, group) : undefined
            }
            onMouseEnter={
              maxDimension === "Channel" ? () => handleLegendEnter(channelArea) : undefined
            }
            onMouseLeave={maxDimension === "Channel" ? () => setHoveredLegend(null) : undefined}
          >
            <div className="legendValue">
              <div>
                <div
                  className={`legendHeaderWrapper${
                    maxDimension === "Channel" ? " channelLegendHeaderWrapper" : ""
                  }`}
                >
                  {maxDimension === "Channel" && (
                    <div
                      className="channelLine"
                      style={{
                        backgroundColor: `${activeLine ? activeColor : hoverColor}`,
                      }}
                    />
                  )}
                  <div
                    className={`legendHeader ${
                      activeGroup === group.channel || activeGroup === null ? "" : "inactiveLegend"
                    }`}
                  >
                    {group.channel}
                  </div>
                </div>
                {maxDimension !== "Channel" &&
                  group.areas.map((area, j) => {
                    const isActiveArea = activeLine === null ? true : activeLine === area.name;
                    const isHoveredLegend =
                      hoveredLegend === null ? true : hoveredLegend === area.name;
                    const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
                    const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
                    return (
                      <div
                        key={`satDecayLegendsubHeader${j}`}
                        className="subHeaderWrapper"
                        onClick={() => handleLegendClick(area, group)}
                        onMouseEnter={() => handleLegendEnter(area)}
                        onMouseLeave={() => setHoveredLegend(null)}
                      >
                        <div
                          className="line"
                          style={{
                            backgroundColor: `${activeLine ? activeColor : hoverColor}`,
                          }}
                        />
                        <div className="subHeaderTextWrap">
                          <div className={`subHeader1 ${isActiveArea ? "" : "inactiveLegend"}`}>
                            {area.grouping.platform}
                          </div>
                          {area.grouping.platform &&
                            (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                              <div style={{ whiteSpace: "pre-wrap" }}> – </div>
                            )}
                          <div className={`subHeader2 ${isActiveArea ? "" : "inactiveLegend"}`}>{`${
                            area.grouping.tactic ? area.grouping.tactic : ""
                          }${area.grouping.tactic && area.grouping.brand_nonbrand ? ", " : ""}${
                            area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ""
                          }`}</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        );
      });
    },
    [
      activeLine,
      activeGroup,
      colorMap,
      handleLegendClick,
      handleLegendEnter,
      hoveredLegend,
      maxDimension,
    ]
  );

  const excelDownloadSat = useCallback(() => {
    exportToExcel(responseCurvesData, `${company}_saturation`);
  }, [responseCurvesData, company]);

  const pngDownloadSat = useCallback(async () => {
    await downloadPNG(".modelInsightsContainer .left .chartContainer", `${company}_saturation`);
  }, [company]);

  const excelDownloadDecay = useCallback(() => {
    exportToExcel(adstockData, `${company}_decay`);
  }, [adstockData, company]);

  const pngDownloadDecay = useCallback(async () => {
    await downloadPNG(".modelInsightsContainer .right .chartContainer", `${company}_decay`);
  }, [company]);

  return (
    <div className="modelInsightsWidget">
      <WidgetContainer
        collapsible
        header="Model Insights"
        subHeader="The model estimation process yields business insights related to channel saturation and the lagged nature of consumer response."
      >
        <div className="modelInsightsContainer" style={{ display: "flex" }}>
          <div className="left" style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <ChartContainer
              enableHoverDesign
              title="Saturation Curves"
              height={CHART_CONTAINER_HEIGHT}
              rightActions={
                <>
                  <InfoTooltip size="reg">
                    Most media tactics will exhibit some form of diminishing returns, due to
                    additional investment reaching less qualified audiences and/or additional user
                    frequency having diminished impact. The model training process estimates the
                    shape of these saturation curves on a channel by channel basis. Examining these
                    curves yields insight into the scalability of each channel and the optimality of
                    incremental investments (see above section).
                  </InfoTooltip>
                  <DownloadDropdown size="sm" onClickOptions={[excelDownloadSat, pngDownloadSat]} />
                </>
              }
            >
              {!R.isEmpty(formattedSatData) && (
                <Fragment>
                  <div className="satWrapper">
                    <div className="satChartTitle">{`${
                      outcomeVariable === "revenue" ? "Revenue" : "KPI Volume"
                    }`}</div>
                    <AutoSizer>
                      {({ width, height }) => (
                        <LC width={width} height={height} ref={setChartRef}>
                          <CartesianGrid
                            stroke={CARTESIAN_GRID_STROKE}
                            strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                            vertical={false}
                          />
                          <XAxis
                            axisLine={false}
                            dataKey="spend"
                            height={xAxisHeight}
                            tick={TICK_STYLE}
                            tickFormatter={val => `${currencyFormatter.format(val, 0)}`}
                            type="number"
                          />
                          <YAxis
                            axisLine={false}
                            dataKey="response"
                            tick={TICK_STYLE}
                            tickFormatter={val => {
                              const decimals = val > 1000000 ? 1 : 0;
                              return `${OUTCOME_FORMATTER_TYPE[outcomeVariable].format(
                                val,
                                decimals
                              )}`;
                            }}
                            tickLine={false}
                            width={yAxisWidth}
                          />
                          {activeLine && tooltip}

                          {formattedSatData.newD.map(line => {
                            const isActiveLine =
                              activeLine === null ? true : activeLine === line.name;
                            const isHoveredLegend =
                              hoveredLegend === null ? true : hoveredLegend === line.name;
                            const activeColor = isActiveLine ? colorMap[line.name] : GRAY_COLOR;
                            const hoverColor = isHoveredLegend ? colorMap[line.name] : GRAY_COLOR;

                            if (!isActiveLine && activeLine !== null) {
                              return null;
                            }
                            return (
                              <Line
                                data={line.data}
                                dataKey="response"
                                dot={<CustomizedDot saturationData={formattedSatData} />}
                                isAnimationActive={false}
                                key={line.name}
                                name={line.name}
                                stroke={activeLine ? activeColor : hoverColor}
                                strokeWidth={3}
                              />
                            );
                          })}
                        </LC>
                      )}
                    </AutoSizer>
                  </div>
                  <div className="rightOfChart">
                    <div className="legend">
                      {buildLegendByGroupedArea(
                        R.sortBy(R.compose(R.toLower, R.prop("channel")))(
                          formattedSatData.groupedLines
                        )
                      )}
                    </div>
                    <div className="meanLegendLabel">
                      <svg width="15" height="15" viewBox="0 0 15 15">
                        <polygon points="7.5,0 15,7.5 7.5,15 0,7.5" fill="#1F003F" />
                      </svg>
                      <div className="meanText">
                        Mean <br /> spend
                      </div>
                    </div>
                    <div className="satChartXAxisTitle">Spend</div>
                  </div>
                </Fragment>
              )}
            </ChartContainer>
          </div>
          <div className="right">
            <ChartContainer
              enableHoverDesign
              height={CHART_CONTAINER_HEIGHT}
              rightActions={
                <>
                  <InfoTooltip size="reg">
                    Few consumers, upon being exposed to an advertisement, drop everything and
                    respond immediately; there is almost always some lag between exposure and
                    response. The model training process estimates the extent of this response lag
                    on a channel by channel basis using a technique known as Adstock. Typically
                    (though not always), we see upper-funnel tactics having longer response lags and
                    lower-funnel tactics converting more quickly. Examining these curves can help us
                    understand channel properties and set appropriate expectations for channel
                    investments.
                  </InfoTooltip>
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadDecay, pngDownloadDecay]}
                  />
                </>
              }
              title="Spend Decay Curves"
            >
              <div className="decayWrapper">
                <div className="decayChartTitle">Spend Carryover</div>
                {Object.keys(formattedDecayData).length && (
                  <LineChart
                    customTooltipHeader={val => `Week ${val} Spend Carryover`}
                    data={formattedDecayData?.newD}
                    lineDataKeys={formattedDecayData?.lines}
                    groupedAreas={R.sortBy(R.compose(R.toLower, R.prop("channel")))(
                      formattedDecayData?.groupedLines
                    )}
                    tooltipLabelFormatter={val => capitalizeWords(val, false, true)}
                    tooltipFormatter={val => (val ? `${numberFormatter.format(val, 0)}%` : val)}
                    usePercentageYAxis={true}
                    xAxisDataKey="date"
                    xAxisTickFormatter={val => `Week ${val}`}
                    maxChannelDimension={maxDimension}
                  />
                )}
              </div>
            </ChartContainer>
          </div>
        </div>
      </WidgetContainer>
    </div>
  );
};

export default ModelInsights;
